import { z, ZodError } from 'zod';
import {
  DeviceSyncField,
  type EnsureTerminalBusinessDeviceResponse,
} from '@am3nd-org/pointcraft-types';

import PlatformService from 'src/utils/Services/PlatformDBService';
import { syncDeviceSyncV4Fields } from 'src/utils/Services/device-sync-v4.service';
import type {
  OfflineMutationRecord,
  OfflineMutationOperation,
  NewOfflineMutationRecord,
  OfflineMutationErrorDetails,
  CurrentDeviceTerminalAssignmentMutationPayload,
} from 'src/utils/Services/types';

import { useDeviceContextStore } from 'src/store/DeviceContext';
import { classifyAuthOperationError } from 'src/auth/errors/auth-operation-error';

export const OFFLINE_MUTATION_RETRY_BASE_MS = 1_000;
export const OFFLINE_MUTATION_MAX_BACKOFF_MS = 5 * 60_000;

export const CURRENT_DEVICE_TERMINAL_REFRESH_FIELDS = [
  DeviceSyncField.APPLICATION_DEVICE_CONTEXT,
  DeviceSyncField.TERMINALS,
  DeviceSyncField.BUSINESS_DEVICES,
];

const ERROR_MESSAGE_LIMIT = 300;
const TRANSIENT_FAILURE_KINDS = new Set([
  'NETWORK',
  'TIMEOUT',
  'RATE_LIMIT',
  'SERVER',
  'AUTHENTICATION',
  'UNKNOWN',
]);

const ExistingTerminalEnsureRequestSchema = z
  .object({
    businessDeviceId: z.string().uuid(),
    name: z.string().trim().min(1),
    terminalId: z.string().uuid(),
  })
  .strict();

const CurrentDeviceTerminalAssignmentPayloadSchema = z
  .object({
    request: ExistingTerminalEnsureRequestSchema,
    businessId: z.string().uuid(),
    businessDeviceId: z.string().uuid(),
    businessLocationId: z.string().uuid(),
    previousTerminalId: z.string().uuid().nullable(),
    selectedTerminalId: z.string().uuid(),
  })
  .strict()
  .superRefine((payload, context) => {
    if (payload.request.businessDeviceId !== payload.businessDeviceId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The ensure request must target the scoped business device',
      });
    }
    if (payload.request.terminalId !== payload.selectedTerminalId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The ensure request must target the selected terminal',
      });
    }
  });

class PermanentOfflineMutationError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'PermanentOfflineMutationError';
    this.code = code;
  }
}

type DispatchResult = { responseReceivedAt: string };
type RetryDisposition = {
  retryable: boolean;
  kind: string;
  code?: string;
  httpStatus?: number;
  retryAfterMs?: number;
};

type OfflineMutationHandler<TPayload> = {
  operation: OfflineMutationOperation;
  schemaVersion: 1;
  payloadSchema: z.ZodType<TPayload>;
  idempotentAndReplaySafe: true;
  orderingKey: (payload: TPayload) => string;
  dispatch: (payload: TPayload) => Promise<DispatchResult>;
  classifyFailure: (error: unknown) => RetryDisposition;
  reconciliation: 'DEVICE_SYNC_AUTHORITATIVE';
};

function validateTerminalAssignmentResponse(
  payload: CurrentDeviceTerminalAssignmentMutationPayload,
  response: EnsureTerminalBusinessDeviceResponse
): void {
  if (response.terminalId !== payload.selectedTerminalId) {
    throw new PermanentOfflineMutationError(
      'The terminal assignment response did not match the selected terminal',
      'TERMINAL_IDENTITY_MISMATCH'
    );
  }
  if (response.businessDevice.id !== payload.businessDeviceId) {
    throw new PermanentOfflineMutationError(
      'The terminal assignment response did not match the current device',
      'DEVICE_IDENTITY_MISMATCH'
    );
  }
  if (
    response.businessDevice.businessLocationId !== payload.businessLocationId ||
    response.businessLocation.id !== payload.businessLocationId
  ) {
    throw new PermanentOfflineMutationError(
      'The terminal assignment response did not match the current location',
      'LOCATION_IDENTITY_MISMATCH'
    );
  }
  if (
    response.businessDevice.paymentTerminalId !== payload.selectedTerminalId ||
    response.businessLocation.terminalId !== payload.selectedTerminalId
  ) {
    throw new PermanentOfflineMutationError(
      'The terminal assignment response did not confirm the selected terminal',
      'TERMINAL_CONFIRMATION_MISMATCH'
    );
  }
}

function classifyFailure(error: unknown): RetryDisposition {
  if (
    error instanceof PermanentOfflineMutationError ||
    error instanceof ZodError ||
    (error instanceof Error && error.name === 'TerminalEnsureResponseValidationError')
  ) {
    return {
      retryable: false,
      kind: 'DATA',
      code:
        error instanceof PermanentOfflineMutationError
          ? error.code
          : 'INVALID_SCHEMA',
    };
  }

  const failure = classifyAuthOperationError(error);
  return {
    retryable: TRANSIENT_FAILURE_KINDS.has(failure.kind),
    kind: failure.kind,
    code: failure.code,
    httpStatus: failure.httpStatus,
    retryAfterMs: failure.retryAfterMs,
  };
}

const terminalAssignmentHandler: OfflineMutationHandler<CurrentDeviceTerminalAssignmentMutationPayload> =
  {
    operation: 'CURRENT_DEVICE_TERMINAL_ASSIGNMENT',
    schemaVersion: 1,
    payloadSchema: CurrentDeviceTerminalAssignmentPayloadSchema,
    idempotentAndReplaySafe: true,
    orderingKey: (payload) => `terminal-assignment:${payload.businessDeviceId}`,
    dispatch: async (payload) => {
      // Keep authenticated transport out of status-only consumers and unit-test module graphs.
      const { ensureTerminalBusinessDevice } = await import(
        'src/utils/Services/terminal-v4.service'
      );
      const response = await ensureTerminalBusinessDevice(payload.request);
      validateTerminalAssignmentResponse(payload, response);
      return { responseReceivedAt: new Date().toISOString() };
    },
    classifyFailure,
    reconciliation: 'DEVICE_SYNC_AUTHORITATIVE',
  };

const handlers = {
  CURRENT_DEVICE_TERMINAL_ASSIGNMENT: terminalAssignmentHandler,
} satisfies Record<OfflineMutationOperation, OfflineMutationHandler<any>>;

export type OfflineMutationSummary = {
  pendingCount: number;
  rejectedCount: number;
  latestPending: OfflineMutationRecord | null;
  latestRejected: OfflineMutationRecord | null;
};

export type OfflineMutationDrainResult = OfflineMutationSummary & {
  acceptedMutationIds: string[];
  rejectedMutationIds: string[];
  nextRunAt: string | null;
  reconciliationFailed: boolean;
};

type DrainOptions = {
  businessId: string;
  businessDeviceId: string;
  ignoreBackoff?: boolean;
};

const listeners = new Set<(businessId: string) => void>();
const tenantRecoveryChains = new Map<string, Promise<unknown>>();

function notifyListeners(businessId: string): void {
  listeners.forEach((listener) => listener(businessId));
}

function toBoundedError(
  error: unknown,
  disposition: RetryDisposition,
  occurredAt: string
): OfflineMutationErrorDetails {
  const message = error instanceof Error ? error.message : 'Mutation delivery failed';
  return {
    kind: disposition.kind.slice(0, 64),
    code: disposition.code?.slice(0, 96) ?? null,
    httpStatus: disposition.httpStatus ?? null,
    message: message.slice(0, ERROR_MESSAGE_LIMIT),
    occurredAt,
  };
}

function retryDelayMs(attemptCount: number, retryAfterMs?: number): number {
  const exponential = Math.min(
    OFFLINE_MUTATION_RETRY_BASE_MS * 2 ** Math.max(attemptCount - 1, 0),
    OFFLINE_MUTATION_MAX_BACKOFF_MS
  );
  return Math.min(
    Math.max(exponential, retryAfterMs ?? 0),
    OFFLINE_MUTATION_MAX_BACKOFF_MS
  );
}

function earliestIso(left: string | null, right: string): string {
  return !left || Date.parse(right) < Date.parse(left) ? right : left;
}

function summarize(records: OfflineMutationRecord[]): OfflineMutationSummary {
  const pending = records.filter((record) => record.state === 'PENDING');
  const rejected = records.filter((record) => record.state === 'REJECTED');
  return {
    pendingCount: pending.length,
    rejectedCount: rejected.length,
    latestPending: pending.at(-1) ?? null,
    latestRejected: rejected.at(-1) ?? null,
  };
}

function getHandler(
  record: OfflineMutationRecord
): OfflineMutationHandler<any> | undefined {
  const registeredHandlers = handlers as Partial<
    Record<string, OfflineMutationHandler<any>>
  >;
  return registeredHandlers[record.operation];
}

async function listScopedRecords(
  businessId: string,
  businessDeviceId: string
): Promise<OfflineMutationRecord[]> {
  const database = await PlatformService.getInstance();
  const records = await database.listOfflineMutations(businessId);
  return records
    .filter((record) => record.businessDeviceId === businessDeviceId)
    .sort((left, right) => left.sequence - right.sequence);
}

export async function enqueueOfflineMutation(
  operation: 'CURRENT_DEVICE_TERMINAL_ASSIGNMENT',
  payload: CurrentDeviceTerminalAssignmentMutationPayload
): Promise<OfflineMutationRecord> {
  const handler = handlers[operation];
  const validatedPayload = handler.payloadSchema.parse(payload);
  const now = new Date().toISOString();
  const newRecord: NewOfflineMutationRecord = {
    mutationId: crypto.randomUUID(),
    schemaVersion: handler.schemaVersion,
    operation,
    state: 'PENDING',
    businessId: validatedPayload.businessId,
    businessDeviceId: validatedPayload.businessDeviceId,
    orderingKey: handler.orderingKey(validatedPayload),
    reconciliationPending: false,
    payload: validatedPayload,
    attemptCount: 0,
    createdAt: now,
    updatedAt: now,
    lastAttemptAt: null,
    nextAttemptAt: null,
    lastError: null,
  };
  const database = await PlatformService.getInstance();
  const record = await database.insertOfflineMutation(newRecord);
  notifyListeners(record.businessId);
  return record;
}

async function drainInternal(options: DrainOptions): Promise<OfflineMutationDrainResult> {
  const database = await PlatformService.getInstance();
  const records = await listScopedRecords(options.businessId, options.businessDeviceId);
  const blockedOrderingKeys = new Set<string>();
  const acceptedRecords: OfflineMutationRecord[] = [];
  const rejectedReconciliationRecords: OfflineMutationRecord[] = [];
  const rejectedMutationIds: string[] = [];
  const affectedOrderingKeys = new Set<string>();
  let nextRunAt: string | null = null;

  await records.reduce(async (previousRecord, storedRecord) => {
    await previousRecord;
    if (storedRecord.state === 'REJECTED') {
      if (!storedRecord.reconciliationPending) return;
      if (
        !options.ignoreBackoff &&
        storedRecord.nextAttemptAt &&
        Date.parse(storedRecord.nextAttemptAt) > Date.now()
      ) {
        nextRunAt = earliestIso(nextRunAt, storedRecord.nextAttemptAt);
        return;
      }
      rejectedReconciliationRecords.push(storedRecord);
      affectedOrderingKeys.add(storedRecord.orderingKey);
      return;
    }
    if (blockedOrderingKeys.has(storedRecord.orderingKey)) return;

    if (storedRecord.reconciliationPending) {
      if (
        !options.ignoreBackoff &&
        storedRecord.nextAttemptAt &&
        Date.parse(storedRecord.nextAttemptAt) > Date.now()
      ) {
        nextRunAt = earliestIso(nextRunAt, storedRecord.nextAttemptAt);
        return;
      }
      acceptedRecords.push(storedRecord);
      affectedOrderingKeys.add(storedRecord.orderingKey);
      return;
    }

    const handler = getHandler(storedRecord);
    const parsedPayload = handler?.payloadSchema.safeParse(storedRecord.payload);
    if (
      !handler ||
      !parsedPayload ||
      storedRecord.schemaVersion !== handler.schemaVersion ||
      !handler.idempotentAndReplaySafe ||
      !parsedPayload.success ||
      handler.orderingKey(
        parsedPayload.success ? parsedPayload.data : storedRecord.payload
      ) !== storedRecord.orderingKey
    ) {
      const now = new Date().toISOString();
      const rejected: OfflineMutationRecord = {
        ...storedRecord,
        state: 'REJECTED',
        reconciliationPending: true,
        updatedAt: now,
        nextAttemptAt: null,
        lastError: {
          kind: 'DATA',
          code: 'INVALID_STORED_MUTATION',
          httpStatus: null,
          message: 'The saved mutation no longer matches its registered operation schema',
          occurredAt: now,
        },
      };
      await database.updateOfflineMutation(rejected);
      rejectedMutationIds.push(rejected.mutationId);
      rejectedReconciliationRecords.push(rejected);
      affectedOrderingKeys.add(rejected.orderingKey);
      return;
    }

    if (
      !options.ignoreBackoff &&
      storedRecord.nextAttemptAt &&
      Date.parse(storedRecord.nextAttemptAt) > Date.now()
    ) {
      blockedOrderingKeys.add(storedRecord.orderingKey);
      nextRunAt = earliestIso(nextRunAt, storedRecord.nextAttemptAt);
      return;
    }

    const attemptTime = new Date().toISOString();
    const attempting: OfflineMutationRecord = {
      ...storedRecord,
      attemptCount: storedRecord.attemptCount + 1,
      updatedAt: attemptTime,
      lastAttemptAt: attemptTime,
      nextAttemptAt: null,
      lastError: null,
    };
    await database.updateOfflineMutation(attempting);

    try {
      await handler.dispatch(parsedPayload.data);
      const accepted: OfflineMutationRecord = {
        ...attempting,
        reconciliationPending: true,
        updatedAt: new Date().toISOString(),
      };
      await database.updateOfflineMutation(accepted);
      acceptedRecords.push(accepted);
      affectedOrderingKeys.add(accepted.orderingKey);
    } catch (error) {
      const disposition = handler.classifyFailure(error);
      const failedAt = new Date().toISOString();
      if (!disposition.retryable) {
        const rejected: OfflineMutationRecord = {
          ...attempting,
          state: 'REJECTED',
          reconciliationPending: true,
          updatedAt: failedAt,
          nextAttemptAt: null,
          lastError: toBoundedError(error, disposition, failedAt),
        };
        await database.updateOfflineMutation(rejected);
        rejectedMutationIds.push(rejected.mutationId);
        rejectedReconciliationRecords.push(rejected);
        affectedOrderingKeys.add(rejected.orderingKey);
        return;
      }

      const retryAt = new Date(
        Date.now() + retryDelayMs(attempting.attemptCount, disposition.retryAfterMs)
      ).toISOString();
      await database.updateOfflineMutation({
        ...attempting,
        updatedAt: failedAt,
        nextAttemptAt: retryAt,
        lastError: toBoundedError(error, disposition, failedAt),
      });
      blockedOrderingKeys.add(attempting.orderingKey);
      nextRunAt = earliestIso(nextRunAt, retryAt);
    }
  }, Promise.resolve());

  const recordsAfterDispatch = await listScopedRecords(options.businessId, options.businessDeviceId);
  const acceptedIds = new Set(acceptedRecords.map((record) => record.mutationId));
  const unresolvedPendingKeys = new Set(
    recordsAfterDispatch
      .filter(
        (record) =>
          record.state === 'PENDING' &&
          !record.reconciliationPending &&
          !acceptedIds.has(record.mutationId)
      )
      .map((record) => record.orderingKey)
  );
  const canReconcile = Array.from(affectedOrderingKeys).every(
    (orderingKey) => !unresolvedPendingKeys.has(orderingKey)
  );
  let reconciliationFailed = false;
  const acceptedMutationIds: string[] = [];

  if (affectedOrderingKeys.size > 0 && canReconcile) {
    try {
      await syncDeviceSyncV4Fields(CURRENT_DEVICE_TERMINAL_REFRESH_FIELDS);

      const latestAccepted = acceptedRecords.at(-1);
      if (latestAccepted) {
        const context = useDeviceContextStore.getState();
        if (
          context.businessId !== latestAccepted.payload.businessId ||
          context.businessDeviceId !== latestAccepted.payload.businessDeviceId ||
          context.businessLocationId !== latestAccepted.payload.businessLocationId ||
          context.terminalId !== latestAccepted.payload.selectedTerminalId
        ) {
          throw new Error('Authoritative Device Sync did not confirm the terminal assignment');
        }
      }

      await acceptedRecords.reduce(async (previousDeletion, accepted) => {
        await previousDeletion;
        await database.deleteOfflineMutation(accepted.mutationId);
        acceptedMutationIds.push(accepted.mutationId);
      }, Promise.resolve());

      const reconciledAt = new Date().toISOString();
      const latestRecords = await listScopedRecords(options.businessId, options.businessDeviceId);
      const reconciledRejections = latestRecords.filter(
        (record) =>
          record.state === 'REJECTED' &&
          record.reconciliationPending &&
          affectedOrderingKeys.has(record.orderingKey)
      );
      await reconciledRejections.reduce(async (previousUpdate, record) => {
        await previousUpdate;
        await database.updateOfflineMutation({
          ...record,
          reconciliationPending: false,
          updatedAt: reconciledAt,
          nextAttemptAt: null,
        });
      }, Promise.resolve());

      const highestAcceptedByKey = new Map<string, number>();
      acceptedRecords.forEach((record) => {
        highestAcceptedByKey.set(
          record.orderingKey,
          Math.max(highestAcceptedByKey.get(record.orderingKey) ?? 0, record.sequence)
        );
      });
      const reconciledRecords = await listScopedRecords(options.businessId, options.businessDeviceId);
      const supersededRecords = reconciledRecords.filter((record) => {
        const supersedingSequence = highestAcceptedByKey.get(record.orderingKey);
        return (
          record.state === 'REJECTED' &&
          supersedingSequence !== undefined &&
          record.sequence < supersedingSequence
        );
      });
      await supersededRecords.reduce(async (previousDeletion, record) => {
        await previousDeletion;
        await database.deleteOfflineMutation(record.mutationId);
      }, Promise.resolve());
    } catch (error) {
      reconciliationFailed = true;
      const disposition: RetryDisposition = {
        retryable: true,
        kind: 'RECONCILIATION',
        code: 'DEVICE_SYNC_FAILED',
      };
      const failedAt = new Date().toISOString();
      await acceptedRecords.reduce(async (previousUpdate, accepted) => {
        await previousUpdate;
        const retryAt = new Date(
          Date.now() + retryDelayMs(accepted.attemptCount)
        ).toISOString();
        await database.updateOfflineMutation({
          ...accepted,
          updatedAt: failedAt,
          nextAttemptAt: retryAt,
          lastError: toBoundedError(error, disposition, failedAt),
        });
        nextRunAt = earliestIso(nextRunAt, retryAt);
      }, Promise.resolve());
      await rejectedReconciliationRecords.reduce(async (previousUpdate, rejected) => {
        await previousUpdate;
        const retryAt = new Date(
          Date.now() + retryDelayMs(Math.max(rejected.attemptCount, 1))
        ).toISOString();
        await database.updateOfflineMutation({
          ...rejected,
          reconciliationPending: true,
          updatedAt: failedAt,
          nextAttemptAt: retryAt,
        });
        nextRunAt = earliestIso(nextRunAt, retryAt);
      }, Promise.resolve());
    }
  }

  const remaining = await listScopedRecords(options.businessId, options.businessDeviceId);
  notifyListeners(options.businessId);
  return {
    ...summarize(remaining),
    acceptedMutationIds,
    rejectedMutationIds,
    nextRunAt,
    reconciliationFailed,
  };
}

function runSerializedForTenant<TResult>(
  businessId: string,
  task: () => Promise<TResult>
): Promise<TResult> {
  const previous = tenantRecoveryChains.get(businessId);
  const run = (previous ? previous.catch(() => undefined) : Promise.resolve()).then(task);
  tenantRecoveryChains.set(businessId, run);
  run
    .finally(() => {
      if (tenantRecoveryChains.get(businessId) === run) {
        tenantRecoveryChains.delete(businessId);
      }
    })
    .catch(() => undefined);
  return run;
}

export function drainOfflineMutations(
  options: DrainOptions
): Promise<OfflineMutationDrainResult> {
  return runSerializedForTenant(options.businessId, () => drainInternal(options));
}

export type TerminalMutationBarrierTaskResult<TResult> =
  | { ready: false; barrier: OfflineMutationDrainResult }
  | { ready: true; barrier: OfflineMutationDrainResult; result: TResult };

/**
 * Serializes a payment-recovery task behind terminal assignment for the same tenant. Keeping the
 * barrier check and task inside one tenant lock prevents a payment outbox from interleaving with
 * terminal assignment delivery while the runtime is already online.
 */
export function runAfterTerminalMutationBarrier<TResult>(
  options: DrainOptions,
  task: () => Promise<TResult>
): Promise<TerminalMutationBarrierTaskResult<TResult>> {
  return runSerializedForTenant(options.businessId, async () => {
    const barrier = await drainInternal(options);
    if (barrier.pendingCount > 0 || barrier.reconciliationFailed) {
      return { ready: false, barrier };
    }
    return { ready: true, barrier, result: await task() };
  });
}

export async function getOfflineMutationSummary(
  businessId: string,
  businessDeviceId: string
): Promise<OfflineMutationSummary> {
  return summarize(await listScopedRecords(businessId, businessDeviceId));
}

export async function acknowledgeRejectedOfflineMutation(
  businessId: string,
  businessDeviceId: string,
  mutationId: string
): Promise<boolean> {
  const database = await PlatformService.getInstance();
  const record = (await listScopedRecords(businessId, businessDeviceId)).find(
    (candidate) => candidate.mutationId === mutationId
  );
  if (!record || record.state !== 'REJECTED') return false;
  await database.deleteOfflineMutation(record.mutationId);
  notifyListeners(businessId);
  return true;
}

export function subscribeOfflineMutationOutbox(
  listener: (businessId: string) => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Authenticated recovery barrier. A transient assignment or failed authoritative reconciliation
 * keeps connectivity in recovery/offline mode; permanent records remain visible but do not block.
 */
export async function drainTerminalAssignmentsBeforeOnline(): Promise<void> {
  const context = useDeviceContextStore.getState();
  if (!context.businessId || !context.businessDeviceId) return;
  const result = await drainOfflineMutations({
    businessId: context.businessId,
    businessDeviceId: context.businessDeviceId,
    ignoreBackoff: true,
  });
  if (result.pendingCount > 0 || result.reconciliationFailed) {
    throw new Error('Terminal assignment is still waiting to sync');
  }
}
